import { useMemo } from 'react';

import { FilterValues } from '@/hooks/use-filter-values';
import { GroupBy, usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import {
  adaptPlannerDepartmentsToDepartmentData,
  adaptPlannerMembersToUserData,
  adaptPlannerProjectsToProjectData,
} from '../../mappers';
import usePlannerStore from '../../store';
import { AssignmentType, RowItem, TimeOffBlockType } from '../../types';
import { useAssignmentsByDepartment } from './use-assignments-by-department';
import { useAssignmentsByMember } from './use-assignments-by-member';
import { useAssignmentsByProject } from './use-assignments-by-project';

interface UsePlannerAssignmentsParams {
  groupBy: GroupBy;
  filters: FilterValues['values'];
}

interface UsePlannerAssignmentsReturn {
  rows: RowItem[];
  assignments: Map<string, AssignmentType[]>;
  timeOffs: Map<string, TimeOffBlockType[]>;
  isLoading: boolean;
  /** Previous data is shown while a changed filter/preference refetches — render dimmed, not skeleton */
  isRefreshing: boolean;
  error: Error | null;
  // Vertical pagination (more rows)
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  // Horizontal pagination (past dates - left scroll)
  fetchPreviousDateRange: () => Promise<void>;
  // Horizontal pagination (future dates - right scroll)
  fetchNextDateRange: () => Promise<void>;
  // Total count of items (users/departments/projects)
  total: number;
  // Refetch data
  refetch: () => void;
}

/**
 * Unified hook for fetching planner assignments based on groupBy mode.
 * Conditionally fetches data from the appropriate API and returns a normalized shape.
 *
 * Benefits:
 * - Conditional fetching: Only active hook fetches data
 * - Normalized interface: Consistent return shape regardless of groupBy
 * - Simplified pagination: Single set of handlers for both axes
 * - Reduced component complexity: Business logic moved to hook
 *
 * Dual-axis pagination:
 * - Vertical: fetchNextPage() loads more rows (members/departments/projects)
 * - Horizontal Left: fetchPreviousDateRange() loads past date ranges
 * - Horizontal Right: fetchNextDateRange() loads future date ranges
 */
export function usePlannerAssignments({
  groupBy,
  filters,
}: UsePlannerAssignmentsParams): UsePlannerAssignmentsReturn {
  const isUsers = groupBy === GroupBy.USERS;
  const isDepartments = groupBy === GroupBy.DEPARTMENTS;
  const isProjects = groupBy === GroupBy.PROJECTS;

  // The planner search endpoints are "view all" queries — without AssignmentsViewAll
  // the backend returns 403. Gate every variant so the requests never fire for users
  // who lack the permission (OPS-1561).
  const { hasPermission } = usePermissions();
  const canViewAllAssignments = hasPermission(OpsiumPolicy.AssignmentsViewAll);

  const departmentDateRange = usePlannerStore(state => state.queryDateRanges[GroupBy.DEPARTMENTS]);
  const projectDateRange = usePlannerStore(state => state.queryDateRanges[GroupBy.PROJECTS]);

  // Conditionally fetch data based on groupBy mode (and only with permission)
  const memberQuery = useAssignmentsByMember({
    filters,
    enabled: isUsers && canViewAllAssignments,
  });

  const departmentQuery = useAssignmentsByDepartment({
    filters,
    enabled: isDepartments && canViewAllAssignments,
  });

  const projectQuery = useAssignmentsByProject({
    filters,
    enabled: isProjects && canViewAllAssignments,
  });

  // Select active query based on groupBy
  const getActiveQuery = () => {
    if (isUsers) return memberQuery;
    if (isDepartments) return departmentQuery;
    if (isProjects) return projectQuery;

    return null;
  };

  const activeQuery = getActiveQuery();

  // Transform data based on groupBy mode
  const { rows, assignments, timeOffs } = useMemo(() => {
    if (isUsers && memberQuery.data) {
      return adaptPlannerMembersToUserData(memberQuery.data.items);
    }

    if (isDepartments && departmentQuery.data) {
      return adaptPlannerDepartmentsToDepartmentData(
        departmentQuery.data.items,
        departmentDateRange
      );
    }

    if (isProjects && projectQuery.data) {
      return adaptPlannerProjectsToProjectData(projectQuery.data.items, projectDateRange);
    }

    return { rows: [], assignments: new Map(), timeOffs: new Map() };
  }, [
    isUsers,
    isDepartments,
    isProjects,
    memberQuery.data,
    departmentQuery.data,
    projectQuery.data,
    departmentDateRange,
    projectDateRange,
  ]);

  // Normalize pagination handlers for vertical scrolling (more rows)
  const fetchNextPage = () => {
    if (!activeQuery) return;

    if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
      activeQuery.fetchNextPage();
    }
  };

  // Normalize pagination handler for horizontal left scrolling (past dates)
  const fetchPreviousDateRange = async () => {
    if (!activeQuery) return;

    await activeQuery.fetchPreviousDateRange();
  };

  // Normalize pagination handler for horizontal right scrolling (future dates)
  const fetchNextDateRange = async () => {
    if (!activeQuery) return;

    await activeQuery.fetchNextDateRange();
  };

  // Get total count from active query
  const total = activeQuery?.data?.pagination?.total ?? 0;

  const refetch = () => {
    activeQuery?.refetch();
  };

  return {
    rows,
    assignments,
    timeOffs,
    // isPending (no data yet) instead of isLoading (pending AND fetching): while the
    // query is still idle/bootstrapping (e.g. queryDateRanges not initialized yet)
    // isLoading is false, which used to flash the bare planner chrome without a
    // skeleton before the first fetch started.
    isLoading: activeQuery ? activeQuery.isPending : false,
    isRefreshing: activeQuery?.isRefreshing ?? false,
    error: (activeQuery?.error as Error) ?? null,
    // Vertical pagination (more rows)
    fetchNextPage,
    hasNextPage: activeQuery?.hasNextPage ?? false,
    isFetchingNextPage: activeQuery?.isFetchingNextPage ?? false,
    // Horizontal pagination (past dates - left scroll)
    fetchPreviousDateRange,
    // Horizontal pagination (future dates - right scroll)
    fetchNextDateRange,
    // Total count
    total,
    // Refetch data
    refetch,
  };
}
